/** ALP-86: admin AI activity per org / user (Langfuse) + the org's AI models (client-owned keys, routing per use case). */
export const aiOrg = {
  page: {
    back: "Organizations",
    backUsers: "Users",
    title: "AI",
    userTitle: "AI activity",
    platformTitle: "AI activity",
    platformHint: "Every AI call of every organization, from Langfuse. Open an organization or a user for their own view.",
    tabs: { activity: "AI activity", models: "AI models" },
  },
  links: {
    org: "AI activity & models",
    orgHint: "Traces, usage, client-owned keys and routing",
    userActivity: "AI activity",
  },
  activity: {
    title: "AI activity",
    description: "Every AI call of this scope, traced in Langfuse: feature, model, tokens, cost, latency and steps.",
    range: "Time range",
    ranges: { "24h": "24 hours", "7d": "7 days", "30d": "30 days" },
    feature: "Feature",
    allFeatures: "All features",
    model: "Model",
    allModels: "All models",
    status: "Status",
    statuses: { all: "All", ok: "OK", error: "Errors" },
    member: "Member",
    allMembers: "All members",
    org: "Organization",
    allOrgs: "All organizations",
    stats: { calls: "Model calls", tokens: "Tokens", cost: "Cost", errors: "Errors" },
    perDay: "Calls and cost per day",
    calls: "Calls",
    cost: "Cost",
    byFeature: "By feature",
    byModel: "By model",
    traces: "Traces",
    automation: "Automation",
    unknownUser: "Unknown user",
    byok: "Own key",
    steps_one: "{{count}} step",
    steps_other: "{{count}} steps",
    empty: "No AI calls in this range",
    emptyHint: "Try a longer time range or clear the filters.",
    disabledTitle: "AI tracing isn't readable here",
    disabledHint: "Set LANGFUSE_URL, LANGFUSE_PUBLIC_KEY and LANGFUSE_SECRET_KEY on the API to see traces. The keys stay on the server.",
    openLangfuse: "Open in Langfuse",
    col: { feature: "Feature", user: "User", model: "Model", tokens: "Tokens", cost: "Cost", latency: "Latency", status: "Status", when: "When" },
  },
  trace: {
    title: "Trace",
    totals: "Totals",
    steps: "Steps",
    input: "Input",
    output: "Output",
    metadata: "Metadata",
    statusMessage: "Status message",
    noSteps: "No steps recorded",
    scrubbed: "Secrets, tokens and provider names are removed before display.",
    latency: "Latency",
    tokens: "Tokens",
    cost: "Cost",
    model: "Model",
    session: "Session",
  },
  models: {
    title: "AI models",
    description: "Client-owned provider keys (BYOK) and which model serves each use case for this organization.",
    unavailable: {
      feature_off: "Client-owned keys aren't enabled for this organization",
      feature_offHint: "Turn on the “Bring your own AI key” plan feature with an override in Plan & entitlements.",
      openPlan: "Open Plan & entitlements",
      no_gateway_db: "Unavailable in this environment",
      no_gateway_dbHint: "The AI gateway has no database here (LiteLLM needs a database with STORE_MODEL_IN_DB), so org-private models can't be created.",
      gateway_unreachable: "The AI gateway can't be reached",
      gateway_unreachableHint: "Connections and routing can be changed once the gateway is back.",
    },
    connections: "Provider connections",
    connectionsHint: "Keys are encrypted at rest and only a masked hint is ever shown.",
    add: "Add connection",
    noConnections: "No connections yet",
    noConnectionsHint: "Add the client's own key to route their use cases to their models.",
    providers: { openai: "OpenAI", anthropic: "Anthropic", azure_openai: "Azure OpenAI", openai_compatible: "OpenAI-compatible" },
    providerHints: {
      openai: "api.openai.com with the client's key",
      anthropic: "Claude models with the client's key",
      azure_openai: "The client's Azure resource and deployments",
      openai_compatible: "Any OpenAI-compatible base URL",
    },
    status: { untested: "Not tested", ok: "Working", error: "Failing" },
    modelStatus: { ready: "Ready", error: "Error" },
    defaultHost: "Provider default",
    key: "Key",
    lastTest: "Last test {{when}}",
    neverTested: "Never tested",
    test: "Test",
    testOk: "Connection works ({{ms}} ms)",
    testFailed: "Connection failed",
    rotate: "Rotate key",
    edit: "Edit",
    delete: "Delete",
    actionsFor: "Actions for {{name}}",
    form: {
      addTitle: "Add a provider connection",
      addDescription: "The client's key goes to the AI gateway as org-private models; no other organization can use them.",
      editTitle: "Edit connection",
      provider: "Provider",
      name: "Name",
      namePlaceholder: "e.g. Client OpenAI",
      apiKey: "API key",
      apiKeyHint: "Write-only: stored encrypted, never shown again.",
      apiBase: "Base URL",
      apiBaseHint: "Required for Azure OpenAI and OpenAI-compatible providers.",
      apiVersion: "API version",
      models: "Models",
      modelId: "Model id",
      modelIdHint: "As the provider names it (for Azure: the deployment name).",
      label: "Label",
      kind: "Kind",
      kinds: { chat: "Chat", embedding: "Embeddings", image: "Images" },
      priceIn: "$ / 1M input",
      priceOut: "$ / 1M output",
      addModel: "Add model",
      removeModel: "Remove model {{n}}",
      reason: "Reason",
      reasonHint: "Audited with the change.",
      create: "Add connection",
      save: "Save",
      created: "Connection added",
      saved: "Connection saved",
      invalid: "Fill in the provider, name, key, models and a reason (3+ characters).",
      baseRequired: "A base URL is required for this provider.",
    },
    rotateTitle: "Rotate the key of {{name}}",
    rotateBody: "The new key replaces the old one in the gateway right away.",
    newKey: "New API key",
    rotated: "Key rotated",
    deleteTitle: "Delete {{name}}?",
    deleteBody: "Its models are removed from the gateway and every use case routed to them falls back to the platform model.",
    deleted: "Connection deleted",
    routing: "Routing per use case",
    routingHint: "Each use case runs on the platform model or one of this organization's models.",
    useCase: "Use case",
    servedBy: "Model",
    platformDefault: "Platform default ({{name}})",
    locked: "Platform only",
    lockedHint: {
      copilot: "The Assistant runs as a Dify chatflow on the platform model; Dify can't switch the model or key per organization.",
      embed: "Search by meaning embeds inside Dify Knowledge with the platform model.",
    },
    useCases: {
      classify: "Comment classification",
      reply_draft: "Reply drafts",
      caption: "Captions and post score",
      copilot: "Assistant",
      image: "Images",
      embed: "Embeddings",
      agents: "AI agents",
    },
    saveRoutes: "Save routing",
    routesTitle: "Save the routing",
    routesBody: "New AI calls of this organization use the selected models.",
    routesSaved: "Routing saved",
    changes_one: "{{count}} change",
    changes_other: "{{count}} changes",
    spend: "Spend this month",
    spendHint: "Spend on the client's own keys doesn't count against the Malfak AI budget.",
    malfakSpend: "Malfak AI budget",
    byokSpend: "Own keys",
    byokTokens: "Own-key tokens",
    keys: "Gateway keys",
    mainScoped: "Malfak key limited to platform models",
    mainUnscoped: "Malfak key not yet limited to platform models",
    byokKey: "Own-models key",
    noByokKey: "Created with the first connection",
  },
};
